// Klymart — Legal copy (Privacy + Contact) in EN/DE.
// Kept separate from messages.js to keep that file lean.

export const COMPANY = {
  legalName: 'Klymart Green Fintech Society',
  street: 'Mirbachstr. 16',
  city: '53173 Bonn',
  country: { en: 'Germany', de: 'Deutschland' },
  responsible: 'Jacques René Nyembe',
  email: 'hello@klymart.com',
  privacyEmail: 'privacy@klymart.com',
};

export const PRIVACY_LAST_UPDATED = {
  en: 'Last updated: June 2026',
  de: 'Zuletzt aktualisiert: Juni 2026',
};

// Set this to a real value once registration is complete, e.g. "VR 12345, Amtsgericht Bonn".
export const REGISTER_ENTRY = '[Vereinsregister: Eintragung in Vorbereitung]';
// Phone is shown as available on request until a public line is published.
export const PUBLIC_PHONE = null;

export const legal = {
  en: {
    privacy: {
      eyebrow: 'Legal',
      title: 'Privacy Policy',
      intro:
        'Klymart takes the protection of personal data seriously. This Privacy Policy explains how we collect, use and protect your personal data when you visit our pre‑launch website or join our waitlist. We comply with the EU General Data Protection Regulation (GDPR / Regulation (EU) 2016/679) and the German Federal Data Protection Act (BDSG).',
      sections: [
        {
          id: 'controller',
          title: '1. Data controller',
          body:
            'The controller responsible for the processing of personal data on this website within the meaning of Art. 4(7) GDPR is:',
          contactBlock: true,
        },
        {
          id: 'data-collected',
          title: '2. Personal data we collect',
          body:
            'We only collect personal data that you actively provide to us when you join the waitlist or contact us. Specifically:',
          bullets: [
            'Brand / company name',
            'Contact person’s full name',
            'Business email address',
            'Website (optional)',
            'Approximate revenue range (optional)',
            'Sustainability certifications of interest (optional)',
            'Free‑text notes you choose to share (optional)',
            'Date and time of submission',
          ],
          footer:
            'We do not use third‑party analytics, advertising trackers or social plugins on this pre‑launch site. The only browser storage we use is a local language preference (key: klymart_locale) which contains no personal data.',
        },
        {
          id: 'purposes',
          title: '3. Purposes of processing',
          body: 'We process the data above only for the following purposes:',
          bullets: [
            'To respond to your waitlist request and reach out when Klymart opens early partnerships.',
            'To prepare and discuss a possible business relationship (pre‑contractual measures).',
            'To occasionally inform you about our launch — only with your consent, and you can unsubscribe anytime.',
            'To comply with our legal obligations (e.g. retention of business correspondence).',
          ],
        },
        {
          id: 'legal-basis',
          title: '4. Legal basis',
          body:
            'The legal basis for processing your personal data is Art. 6(1)(a) GDPR (consent — which you give by submitting the waitlist form) and Art. 6(1)(b) GDPR (steps taken at your request prior to entering into a contract). Where we are legally required to keep records, the basis is Art. 6(1)(c) GDPR.',
        },
        {
          id: 'recipients',
          title: '5. Recipients & data transfers',
          body:
            'Your data is processed within the European Union by Klymart and by carefully selected service providers acting as processors under Art. 28 GDPR (e.g. hosting provider, email service). We do not sell your data. We do not transfer personal data to third countries outside the EU/EEA. If this changes in the future, we will rely on appropriate safeguards under Art. 44 et seq. GDPR (such as EU Standard Contractual Clauses) and update this notice.',
        },
        {
          id: 'retention',
          title: '6. Retention period',
          body:
            'Waitlist data is stored as long as needed to evaluate a possible partnership, and at most for 24 months from your last contact with us. You may request earlier deletion at any time (see Section 8). Statutory retention periods (e.g. tax/commercial law) prevail where applicable.',
        },
        {
          id: 'security',
          title: '7. Security',
          body:
            'We use industry‑standard technical and organisational measures to protect your data, including encrypted transport (HTTPS/TLS), encrypted password hashes, access controls and the principle of least privilege.',
        },
        {
          id: 'rights',
          title: '8. Your rights as a data subject',
          body: 'Under the GDPR you have the right to:',
          bullets: [
            'Access — obtain confirmation and a copy of the personal data we hold about you (Art. 15).',
            'Rectification — have inaccurate data corrected (Art. 16).',
            'Erasure — have your data deleted ("right to be forgotten", Art. 17).',
            'Restriction of processing (Art. 18).',
            'Data portability — receive your data in a structured, machine‑readable format (Art. 20).',
            'Object to processing based on legitimate interests (Art. 21).',
            'Withdraw your consent at any time, with effect for the future (Art. 7(3)).',
          ],
          footer:
            'To exercise any of these rights, simply contact us using the details in Section 1 or write to ' + COMPANY.privacyEmail + '. We will respond within one month.',
        },
        {
          id: 'complaint',
          title: '9. Right to lodge a complaint',
          body:
            'You have the right to lodge a complaint with a supervisory authority if you believe we have not handled your personal data lawfully. The competent authority for Klymart in Germany is:',
          bullets: [
            'Landesbeauftragte für Datenschutz und Informationsfreiheit Nordrhein‑Westfalen (LDI NRW)',
            'Kavalleriestr. 2–4, 40213 Düsseldorf, Germany',
            'poststelle@ldi.nrw.de',
          ],
        },
        {
          id: 'cookies',
          title: '10. Cookies & local storage',
          body:
            'This website does not use tracking cookies. We use a single localStorage entry to remember your preferred language ("klymart_locale"). It is stored only on your device and contains no personal data. No consent banner is required because no non‑essential data is collected.',
        },
        {
          id: 'changes',
          title: '11. Changes to this policy',
          body:
            'We may update this Privacy Policy to reflect changes to our service or legal requirements. The latest version is always available on this page with the date below.',
        },
      ],
    },
    contact: {
      eyebrow: 'Contact',
      title: 'Get in touch',
      lead:
        'For partnership enquiries, press requests or anything else — reach out directly. We typically reply within two business days.',
      generalTitle: 'General enquiries',
      privacyTitle: 'Data protection',
      privacyBody:
        'For any question about your personal data or to exercise your GDPR rights, contact us at ',
      addressTitle: 'Postal address',
      responsibleLabel: 'Responsible',
      writeEmail: 'Write an email',
      backHome: 'Back to home',
    },
    impressum: {
      eyebrow: 'Legal notice',
      title: 'Legal notice (Impressum)',
      intro:
        'Information pursuant to § 5 of the German Telemedia Act (TMG) and § 18(2) of the Interstate Media Treaty (MStV).',
      sections: [
        { id: 'provider', title: '1. Service provider',
          body: 'The provider of this website is:',
          contactBlock: true },
        { id: 'contact', title: '2. Contact',
          bullets: [
            'Email: ' + COMPANY.email,
            'Phone: available on request',
          ] },
        { id: 'register', title: '3. Register entry',
          body: REGISTER_ENTRY },
        { id: 'vat', title: '4. VAT identification number',
          body: 'No VAT identification number under § 27a of the German Value Added Tax Act has been issued at this time.' },
        { id: 'content-responsible', title: '5. Responsible for the content under § 18(2) MStV',
          body: COMPANY.responsible + ', ' + COMPANY.street + ', ' + COMPANY.city + ', Germany.' },
        { id: 'odr', title: '6. EU online dispute resolution',
          body:
            'The European Commission provides a platform for online dispute resolution (ODR), available at https://ec.europa.eu/consumers/odr. We are not obliged and not willing to participate in dispute resolution proceedings before a consumer arbitration board.' },
        { id: 'liability-content', title: '7. Liability for content',
          body:
            'As a service provider we are responsible for our own content on these pages in accordance with general legislation pursuant to § 7(1) TMG. According to §§ 8 to 10 TMG, however, we are not obliged to monitor transmitted or stored third‑party information or to investigate circumstances that indicate illegal activity. Obligations to remove or block the use of information under general laws remain unaffected.' },
        { id: 'liability-links', title: '8. Liability for links',
          body:
            'Our website contains links to external third‑party websites over whose content we have no control. We therefore cannot accept any liability for this third‑party content. The respective provider or operator of the linked pages is always responsible for their content. The linked pages were checked for possible legal violations at the time of linking; illegal content was not recognisable at the time the link was created. If we become aware of legal violations we will remove such links immediately.' },
        { id: 'copyright', title: '9. Copyright',
          body:
            'The content and works on these pages created by the site operators are subject to German copyright law. Duplication, processing, distribution and any kind of exploitation outside the limits of copyright law require the written consent of the respective author or creator.' },
      ],
    },
    terms: {
      eyebrow: 'Legal',
      title: 'Terms of Service',
      intro:
        'These Terms of Service ("Terms") govern your use of the Klymart pre‑launch website and waitlist offered by Klymart Green Fintech Society ("Klymart", "we", "us"). By using the site or submitting the waitlist form you agree to these Terms.',
      sections: [
        { id: 'scope', title: '1. Scope',
          body:
            'These Terms apply to all visitors of klymart.com and to all brands who join the Klymart waitlist. They do not constitute a contract for any commercial service. A separate contract will govern any future partnership between Klymart and a brand.' },
        { id: 'services', title: '2. Pre‑launch nature of the service',
          body:
            'Klymart is currently in a pre‑launch phase. The information on this website describes our planned offering and is provided for informational purposes only. Joining the waitlist does not create a binding contractual relationship and gives no guarantee of access to any future product or service.' },
        { id: 'eligibility', title: '3. Eligibility',
          body:
            'You may use this website only if you are legally able to enter into binding agreements under the law of your jurisdiction. The waitlist is intended for businesses; the information you provide must relate to a legitimate brand or organisation.' },
        { id: 'obligations', title: '4. Your obligations',
          body:
            'You agree to provide accurate, current and complete information; not to use the site for any unlawful purpose; not to attempt to gain unauthorised access to our systems; and not to interfere with the normal operation of the website.' },
        { id: 'ip', title: '5. Intellectual property',
          body:
            'All content on this website (including text, graphics, logos, the Klymart name and brand assets) is the property of Klymart or its licensors and is protected by intellectual property law. You may not copy, modify or redistribute it without our prior written consent, except as permitted by mandatory statutory rights.' },
        { id: 'disclaimer', title: '6. Disclaimer & limitation of liability',
          body:
            'The website is provided "as is" without warranties of any kind, to the maximum extent permitted by applicable law. Klymart shall be liable only for damages caused by intent or gross negligence, for damages caused by simple negligence resulting from the breach of a material contractual obligation, and within the scope of mandatory statutory liability (e.g. under the German Product Liability Act and for personal injury).' },
        { id: 'data', title: '7. Data protection',
          body:
            'Your personal data is processed in accordance with our Privacy Policy, which forms an integral part of these Terms.' },
        { id: 'changes', title: '8. Changes to these Terms',
          body:
            'We may update these Terms from time to time. The current version will always be available on this page with the date of last update.' },
        { id: 'law', title: '9. Governing law and place of jurisdiction',
          body:
            'These Terms are governed by the laws of the Federal Republic of Germany, excluding the UN Convention on Contracts for the International Sale of Goods. To the extent legally permissible, the exclusive place of jurisdiction is Bonn, Germany.' },
        { id: 'severability', title: '10. Severability',
          body:
            'If any provision of these Terms is held to be invalid or unenforceable, the remaining provisions shall remain in full force and effect.' },
        { id: 'contact', title: '11. Contact',
          body:
            'For questions about these Terms, please contact us at ' + COMPANY.email + '.' },
      ],
    },
  },

  de: {
    privacy: {
      eyebrow: 'Rechtliches',
      title: 'Datenschutzerklärung',
      intro:
        'Klymart nimmt den Schutz personenbezogener Daten ernst. Diese Datenschutzerklärung beschreibt, wie wir personenbezogene Daten erheben, verwenden und schützen, wenn Sie unsere Pre‑Launch‑Website besuchen oder sich auf unsere Warteliste eintragen. Wir handeln im Einklang mit der EU‑Datenschutz‑Grundverordnung (DSGVO / Verordnung (EU) 2016/679) und dem Bundesdatenschutzgesetz (BDSG).',
      sections: [
        {
          id: 'controller',
          title: '1. Verantwortlicher',
          body:
            'Verantwortlicher für die Verarbeitung personenbezogener Daten auf dieser Website im Sinne von Art. 4 Abs. 7 DSGVO ist:',
          contactBlock: true,
        },
        {
          id: 'data-collected',
          title: '2. Welche Daten wir erheben',
          body:
            'Wir erheben ausschließlich personenbezogene Daten, die Sie uns aktiv mitteilen, wenn Sie sich auf die Warteliste eintragen oder uns kontaktieren. Insbesondere:',
          bullets: [
            'Marken‑ bzw. Firmenname',
            'Vor‑ und Nachname der Ansprechperson',
            'Geschäftliche E‑Mail‑Adresse',
            'Website (optional)',
            'Umsatzgrößenbereich (optional)',
            'Zertifizierungen von Interesse (optional)',
            'Freitext‑Notizen, sofern Sie welche mitteilen (optional)',
            'Datum und Uhrzeit der Eingabe',
          ],
          footer:
            'Wir setzen auf dieser Pre‑Launch‑Website keinerlei Analyse‑Dienste, Werbe‑Tracker oder Social‑Plugins ein. Im Browser speichern wir lediglich Ihre Sprachpräferenz (Schlüssel: klymart_locale) — dieser Eintrag enthält keine personenbezogenen Daten.',
        },
        {
          id: 'purposes',
          title: '3. Zwecke der Verarbeitung',
          body: 'Wir verarbeiten die oben genannten Daten ausschließlich zu folgenden Zwecken:',
          bullets: [
            'Bearbeitung Ihrer Warteliste‑Anfrage und Kontaktaufnahme, sobald Klymart frühe Partnerschaften öffnet.',
            'Anbahnung und Besprechung einer möglichen Geschäftsbeziehung (vorvertragliche Maßnahmen).',
            'Information über unseren Launch — nur mit Ihrer Einwilligung; eine Abmeldung ist jederzeit möglich.',
            'Erfüllung gesetzlicher Pflichten (z. B. Aufbewahrung geschäftlicher Korrespondenz).',
          ],
        },
        {
          id: 'legal-basis',
          title: '4. Rechtsgrundlage',
          body:
            'Rechtsgrundlage der Verarbeitung sind Art. 6 Abs. 1 lit. a DSGVO (Einwilligung — die Sie mit Absenden des Formulars erteilen) und Art. 6 Abs. 1 lit. b DSGVO (vorvertragliche Maßnahmen auf Ihre Anfrage hin). Soweit gesetzliche Aufbewahrungspflichten greifen, gilt Art. 6 Abs. 1 lit. c DSGVO.',
        },
        {
          id: 'recipients',
          title: '5. Empfänger & Datenübermittlung',
          body:
            'Ihre Daten werden innerhalb der Europäischen Union durch Klymart sowie durch sorgfältig ausgewählte Dienstleister (Auftragsverarbeiter nach Art. 28 DSGVO, z. B. Hosting‑ und E‑Mail‑Provider) verarbeitet. Wir verkaufen Ihre Daten nicht. Eine Übermittlung in Drittländer außerhalb der EU/des EWR findet nicht statt. Sollte sich dies künftig ändern, geschieht es ausschließlich auf Basis geeigneter Garantien nach Art. 44 ff. DSGVO (z. B. EU‑Standardvertragsklauseln); wir werden diesen Hinweis dann entsprechend aktualisieren.',
        },
        {
          id: 'retention',
          title: '6. Speicherdauer',
          body:
            'Warteliste‑Daten werden so lange gespeichert, wie es zur Prüfung einer möglichen Partnerschaft erforderlich ist, längstens jedoch 24 Monate ab dem letzten Kontakt. Sie können jederzeit eine frühere Löschung verlangen (siehe Ziffer 8). Gesetzliche Aufbewahrungspflichten (z. B. nach HGB/AO) bleiben unberührt.',
        },
        {
          id: 'security',
          title: '7. Sicherheit',
          body:
            'Wir treffen branchenübliche technische und organisatorische Maßnahmen zum Schutz Ihrer Daten, u. a. verschlüsselte Übertragung (HTTPS/TLS), verschlüsselte Speicherung von Passwort‑Hashes, Zugriffskontrollen und das Prinzip der minimalen Berechtigung.',
        },
        {
          id: 'rights',
          title: '8. Ihre Rechte als betroffene Person',
          body: 'Nach der DSGVO stehen Ihnen folgende Rechte zu:',
          bullets: [
            'Auskunft über die zu Ihrer Person gespeicherten Daten (Art. 15).',
            'Berichtigung unrichtiger Daten (Art. 16).',
            'Löschung Ihrer Daten („Recht auf Vergessenwerden“, Art. 17).',
            'Einschränkung der Verarbeitung (Art. 18).',
            'Datenübertragbarkeit — Erhalt in einem strukturierten, gängigen Format (Art. 20).',
            'Widerspruch gegen Verarbeitungen, die auf berechtigten Interessen beruhen (Art. 21).',
            'Widerruf einer erteilten Einwilligung jederzeit mit Wirkung für die Zukunft (Art. 7 Abs. 3).',
          ],
          footer:
            'Zur Ausübung dieser Rechte genügt eine formlose Nachricht an die in Ziffer 1 genannten Kontaktdaten oder an ' + COMPANY.privacyEmail + '. Wir antworten innerhalb eines Monats.',
        },
        {
          id: 'complaint',
          title: '9. Beschwerderecht',
          body:
            'Sie haben das Recht, sich bei einer Datenschutz‑Aufsichtsbehörde zu beschweren, wenn Sie der Auffassung sind, dass wir Ihre Daten nicht rechtmäßig verarbeitet haben. Für Klymart in Deutschland zuständig ist:',
          bullets: [
            'Landesbeauftragte für Datenschutz und Informationsfreiheit Nordrhein‑Westfalen (LDI NRW)',
            'Kavalleriestr. 2–4, 40213 Düsseldorf, Deutschland',
            'poststelle@ldi.nrw.de',
          ],
        },
        {
          id: 'cookies',
          title: '10. Cookies & lokaler Speicher',
          body:
            'Diese Website verwendet keine Tracking‑Cookies. Wir nutzen lediglich einen einzigen localStorage‑Eintrag, um Ihre bevorzugte Sprache zu merken („klymart_locale“). Dieser Eintrag bleibt ausschließlich auf Ihrem Gerät und enthält keine personenbezogenen Daten. Ein Cookie‑Banner ist daher nicht erforderlich, da keine nicht‑essenziellen Daten erhoben werden.',
        },
        {
          id: 'changes',
          title: '11. Änderungen dieser Erklärung',
          body:
            'Wir können diese Datenschutzerklärung anpassen, wenn sich unser Angebot oder die rechtlichen Anforderungen ändern. Die jeweils aktuelle Fassung finden Sie stets auf dieser Seite mit dem unten aufgeführten Datum.',
        },
      ],
    },
    contact: {
      eyebrow: 'Kontakt',
      title: 'Sprechen Sie uns an',
      lead:
        'Für Partner‑Anfragen, Presse oder andere Themen — schreiben Sie uns direkt. Wir antworten in der Regel innerhalb von zwei Werktagen.',
      generalTitle: 'Allgemeine Anfragen',
      privacyTitle: 'Datenschutz',
      privacyBody:
        'Für Fragen zu Ihren personenbezogenen Daten oder zur Ausübung Ihrer DSGVO‑Rechte erreichen Sie uns unter ',
      addressTitle: 'Postanschrift',
      responsibleLabel: 'Verantwortlich',
      writeEmail: 'E‑Mail schreiben',
      backHome: 'Zur Startseite',
    },
    impressum: {
      eyebrow: 'Rechtliches',
      title: 'Impressum',
      intro:
        'Angaben gemäß § 5 TMG und § 18 Abs. 2 MStV.',
      sections: [
        { id: 'provider', title: '1. Diensteanbieter',
          body: 'Anbieter dieser Website ist:',
          contactBlock: true },
        { id: 'contact', title: '2. Kontakt',
          bullets: [
            'E‑Mail: ' + COMPANY.email,
            'Telefon: auf Anfrage',
          ] },
        { id: 'register', title: '3. Registereintrag',
          body: '[Vereinsregister: Eintragung in Vorbereitung]' },
        { id: 'vat', title: '4. Umsatzsteuer‑Identifikationsnummer',
          body: 'Eine Umsatzsteuer‑Identifikationsnummer nach § 27a UStG wurde uns bislang nicht zugeteilt.' },
        { id: 'content-responsible', title: '5. Verantwortlich für den Inhalt nach § 18 Abs. 2 MStV',
          body: COMPANY.responsible + ', ' + COMPANY.street + ', ' + COMPANY.city + ', Deutschland.' },
        { id: 'odr', title: '6. EU‑Streitschlichtung',
          body:
            'Die Europäische Kommission stellt eine Plattform zur Online‑Streitbeilegung (OS) bereit: https://ec.europa.eu/consumers/odr. Unsere E‑Mail‑Adresse finden Sie oben im Impressum. Wir sind nicht bereit und nicht verpflichtet, an Streitbeilegungsverfahren vor einer Verbraucherschlichtungsstelle teilzunehmen.' },
        { id: 'liability-content', title: '7. Haftung für Inhalte',
          body:
            'Als Diensteanbieter sind wir gemäß § 7 Abs. 1 TMG für eigene Inhalte auf diesen Seiten nach den allgemeinen Gesetzen verantwortlich. Nach §§ 8 bis 10 TMG sind wir als Diensteanbieter jedoch nicht verpflichtet, übermittelte oder gespeicherte fremde Informationen zu überwachen oder nach Umständen zu forschen, die auf eine rechtswidrige Tätigkeit hinweisen. Verpflichtungen zur Entfernung oder Sperrung der Nutzung von Informationen nach den allgemeinen Gesetzen bleiben hiervon unberührt.' },
        { id: 'liability-links', title: '8. Haftung für Links',
          body:
            'Unser Angebot enthält Links zu externen Websites Dritter, auf deren Inhalte wir keinen Einfluss haben. Deshalb können wir für diese fremden Inhalte auch keine Gewähr übernehmen. Für die Inhalte der verlinkten Seiten ist stets der jeweilige Anbieter oder Betreiber der Seiten verantwortlich. Die verlinkten Seiten wurden zum Zeitpunkt der Verlinkung auf mögliche Rechtsverstöße überprüft. Rechtswidrige Inhalte waren zum Zeitpunkt der Verlinkung nicht erkennbar. Bei Bekanntwerden von Rechtsverletzungen werden wir derartige Links umgehend entfernen.' },
        { id: 'copyright', title: '9. Urheberrecht',
          body:
            'Die durch die Seitenbetreiber erstellten Inhalte und Werke auf diesen Seiten unterliegen dem deutschen Urheberrecht. Die Vervielfältigung, Bearbeitung, Verbreitung und jede Art der Verwertung außerhalb der Grenzen des Urheberrechtes bedürfen der schriftlichen Zustimmung des jeweiligen Autors bzw. Erstellers.' },
      ],
    },
    terms: {
      eyebrow: 'Rechtliches',
      title: 'Nutzungsbedingungen',
      intro:
        'Diese Nutzungsbedingungen („Bedingungen“) regeln die Nutzung der Pre‑Launch‑Website und der Warteliste von Klymart, betrieben von der Klymart Green Fintech Society („Klymart“, „wir“). Mit der Nutzung der Website oder dem Absenden des Warteliste‑Formulars stimmen Sie diesen Bedingungen zu.',
      sections: [
        { id: 'scope', title: '1. Geltungsbereich',
          body:
            'Diese Bedingungen gelten für alle Besucher:innen von klymart.com und für alle Marken, die sich in die Klymart‑Warteliste eintragen. Sie begründen keinen kommerziellen Vertrag. Eine spätere Partnerschaft zwischen Klymart und einer Marke wird durch einen separaten Vertrag geregelt.' },
        { id: 'services', title: '2. Pre‑Launch‑Charakter des Angebots',
          body:
            'Klymart befindet sich derzeit in einer Pre‑Launch‑Phase. Die Informationen auf dieser Website beschreiben unser geplantes Angebot und dienen ausschließlich Informationszwecken. Die Eintragung in die Warteliste begründet kein verbindliches Vertragsverhältnis und gibt keinen Anspruch auf den Zugang zu einem künftigen Produkt oder einer Dienstleistung.' },
        { id: 'eligibility', title: '3. Voraussetzungen',
          body:
            'Sie dürfen diese Website nur nutzen, wenn Sie nach dem Recht Ihres Aufenthaltsstaates wirksam Verträge schließen können. Die Warteliste richtet sich an Unternehmen; die von Ihnen angegebenen Informationen müssen sich auf eine reale Marke oder Organisation beziehen.' },
        { id: 'obligations', title: '4. Ihre Pflichten',
          body:
            'Sie verpflichten sich, korrekte, aktuelle und vollständige Angaben zu machen, die Website nicht für rechtswidrige Zwecke zu nutzen, keine unbefugten Zugriffsversuche auf unsere Systeme zu unternehmen und den Betrieb der Website nicht zu beeinträchtigen.' },
        { id: 'ip', title: '5. Geistiges Eigentum',
          body:
            'Sämtliche Inhalte dieser Website (insbesondere Texte, Grafiken, Logos, der Name Klymart und Marken‑Assets) sind Eigentum von Klymart oder unseren Lizenzgebern und durch Urheber‑ und Markenrecht geschützt. Eine Vervielfältigung, Bearbeitung oder Weitergabe ist ohne unsere vorherige schriftliche Zustimmung unzulässig, soweit zwingende gesetzliche Rechte nichts anderes bestimmen.' },
        { id: 'disclaimer', title: '6. Haftung',
          body:
            'Die Website wird im gesetzlich zulässigen Rahmen „wie sie ist“ zur Verfügung gestellt. Klymart haftet ausschließlich für Schäden aus Vorsatz oder grober Fahrlässigkeit, für Schäden aus der Verletzung einer wesentlichen Vertragspflicht durch einfache Fahrlässigkeit sowie im Rahmen der zwingenden gesetzlichen Haftung (z. B. nach dem Produkthaftungsgesetz und bei Personenschäden).' },
        { id: 'data', title: '7. Datenschutz',
          body:
            'Ihre personenbezogenen Daten werden gemäß unserer Datenschutzerklärung verarbeitet, die Bestandteil dieser Bedingungen ist.' },
        { id: 'changes', title: '8. Änderungen dieser Bedingungen',
          body:
            'Wir können diese Bedingungen von Zeit zu Zeit anpassen. Die jeweils aktuelle Fassung ist mit Datum der letzten Aktualisierung auf dieser Seite verfügbar.' },
        { id: 'law', title: '9. Anwendbares Recht und Gerichtsstand',
          body:
            'Es gilt das Recht der Bundesrepublik Deutschland unter Ausschluss des UN‑Kaufrechts. Soweit gesetzlich zulässig, ist ausschließlicher Gerichtsstand Bonn, Deutschland.' },
        { id: 'severability', title: '10. Salvatorische Klausel',
          body:
            'Sollte eine Bestimmung dieser Bedingungen unwirksam oder undurchsetzbar sein, bleiben die übrigen Bestimmungen davon unberührt.' },
        { id: 'contact', title: '11. Kontakt',
          body:
            'Bei Fragen zu diesen Bedingungen erreichen Sie uns unter ' + COMPANY.email + '.' },
      ],
    },
  },
};
